import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  RefreshCw,
  Check,
  X,
  ExternalLink,
  ImageIcon,
  PlayCircle,
  CheckSquare,
  Square,
} from "lucide-react";

/** History Review Tab — Session B of the Caribbean Historical Photo
 *  Archive. Surfaces `sensitivity_score === "review"` items from
 *  `/api/admin/history/queue` and lets an admin Approve (→ publish) or
 *  Reject (→ hide), one-at-a-time OR in bulk. The bulk path is a hard
 *  requirement because post-backfill this queue will hold thousands of
 *  items — clicking each is a non-starter.
 *
 *  Papiamento captions are NEVER auto-translated (colonial-dialect
 *  risk — see PRD 2026-02-08). This tab shows raw Commons descriptions
 *  only; auto-translation of NL/ES is deferred until post-backfill.
 *
 *  Mirrors the visual pattern of DedupReviewTab.jsx.
 */
const STATUS_TABS = [
  { id: "review", label: "Needs Review" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "reject", label: "Auto-Blocked" },
];

const PAGE_SIZE = 60;

export const HistoryReviewTab = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("review");
  const [scrapeStarting, setScrapeStarting] = useState(false);
  const [scrapeState, setScrapeState] = useState([]);
  const [pending, setPending] = useState({}); // { photo_id: "approve"|"reject" }
  const [selected, setSelected] = useState(() => new Set());
  const [bulkPending, setBulkPending] = useState(false);
  // Filters
  const [island, setIsland] = useState("");
  const [decade, setDecade] = useState("");
  const [reason, setReason] = useState("");
  const [offset, setOffset] = useState(0);
  const [facets, setFacets] = useState({
    islands: [], decades: [], reason_buckets: [], reasons_granular: [],
  });
  // Live scrape-run tracking. `currentRun` is the running run (if any)
  // OR the most-recently-finished run (used to render the completion
  // banner for ~1 min after finish). Polled every 12s while the tab is
  // mounted. `prevStatusRef` is a ref (not state) so the poll closure
  // sees the actually-latest value between ticks — a state variable
  // would freeze at its initial value because the effect runs only
  // once (empty deps).
  const [currentRun, setCurrentRun] = useState(null);
  const prevStatusRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status, limit: PAGE_SIZE, offset };
      if (island) params.island = island;
      if (decade) params.decade = decade;
      if (reason) params.reason = reason;
      const [q, s, f] = await Promise.all([
        api.get("/admin/history/queue", { params }),
        api.get("/admin/history/scrape-state", { params: { limit: 20 } }),
        api.get("/admin/history/queue/facets", { params: { status } }),
      ]);
      setItems(q.data?.photos || []);
      setTotal(q.data?.total || 0);
      setScrapeState(s.data?.entries || []);
      setFacets({
        islands: f.data?.islands || [],
        decades: f.data?.decades || [],
        reason_buckets: f.data?.reason_buckets || [],
        reasons_granular: f.data?.reasons_granular || [],
      });
      setSelected(new Set()); // clear selection on any reload/filter change
    } catch (e) {
      toast.error("Failed to load history queue");
    } finally {
      setLoading(false);
    }
  }, [status, island, decade, reason, offset]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll `/scrape-runs/current` every 12s. When the endpoint returns
  // a `running` run, the button is disabled + a live progress banner
  // renders above the queue. When the status transitions from
  // `running` → `completed`/`failed`, we toast + refresh the queue so
  // newly-ingested photos appear without another manual refresh.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get("/admin/history/scrape-runs/current");
        if (cancelled) return;
        const run = r.data?.run || null;
        setCurrentRun(run);
        // Fire completion/failure toast exactly once on the running→
        // finished transition. Use the ref so we see the latest value
        // between polls (state would freeze in this closure).
        if (prevStatusRef.current === "running" && run && run.status !== "running") {
          if (run.status === "completed") {
            toast.success(
              `Backfill complete — ${run.totals?.inserted || 0} new · ${run.totals?.updated || 0} updated`
            );
          } else if (run.status === "failed") {
            toast.error(`Backfill failed: ${run.error || "unknown error"}`);
          } else if (run.status === "skipped") {
            toast.warning(
              "Backfill was a no-op — all categories already scraped today. Use 'Force re-scrape' to override.",
              { duration: 8000 }
            );
          }
          load(); // refresh queue to surface newly-ingested photos
        }
        prevStatusRef.current = run?.status || null;
      } catch (_e) {
        // Silent — poll is best-effort. Server hiccup shouldn't spam.
      }
    };
    poll();
    const iv = setInterval(poll, 12000);
    return () => { cancelled = true; clearInterval(iv); };
    // `load` intentionally omitted from deps so the interval doesn't
    // remount on every queue change; a stale `load` reference is fine
    // here (it's a `useCallback` with its own deps and simply re-fetches
    // whatever the current filter state is).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (photoId, decision) => {
    setPending((p) => ({ ...p, [photoId]: decision }));
    try {
      await api.post(`/admin/history/${photoId}/decision`, { decision });
      toast.success(decision === "approve" ? "Approved — now published" : "Rejected");
      // Optimistic remove from current view (assumes we're in "review" tab)
      setItems((prev) => prev.filter((it) => it.id !== photoId));
      setTotal((t) => Math.max(0, t - 1));
      setSelected((prev) => {
        const n = new Set(prev);
        n.delete(photoId);
        return n;
      });
    } catch (e) {
      toast.error(`${decision} failed`);
    } finally {
      setPending((p) => {
        const n = { ...p };
        delete n[photoId];
        return n;
      });
    }
  };

  const bulkDecide = async (decision) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const label = decision === "approve" ? "Approve" : "Reject";
    if (!window.confirm(`${label} ${ids.length} photo${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkPending(true);
    try {
      const r = await api.post("/admin/history/bulk-decision", {
        photo_ids: ids,
        decision,
      });
      const modified = r.data?.modified ?? 0;
      toast.success(
        `${decision === "approve" ? "Approved" : "Rejected"} ${modified} photo${modified === 1 ? "" : "s"}` +
          (modified < ids.length ? ` (${ids.length - modified} already handled)` : "")
      );
      // Optimistic remove all selected from view
      const removeSet = new Set(ids);
      setItems((prev) => {
        const next = prev.filter((it) => !removeSet.has(it.id));
        // If the current page is now empty and we're not on page 1,
        // step back one page — otherwise the user stares at a blank
        // grid until they realise they need to click "Prev". This
        // reload also refreshes the total to reflect the actual DB
        // count (guards against a stale `total - modified` calc if a
        // parallel admin also acted on some of the same ids).
        if (next.length === 0 && offset >= PAGE_SIZE) {
          setOffset((o) => Math.max(0, o - PAGE_SIZE));
        }
        return next;
      });
      setTotal((t) => Math.max(0, t - modified));
      setSelected(new Set());
    } catch (e) {
      toast.error("Bulk decision failed");
    } finally {
      setBulkPending(false);
    }
  };

  const toggleOne = useCallback((photoId) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(photoId)) n.delete(photoId);
      else n.add(photoId);
      return n;
    });
  }, []);

  const allOnPageSelected = useMemo(
    () => items.length > 0 && items.every((it) => selected.has(it.id)),
    [items, selected]
  );

  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      // Clear only ids on the current page (leaves any off-page — but we
      // don't paginate yet, so effectively clears everything)
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((it) => it.id)));
    }
  };

  const isRunning = currentRun?.status === "running";
  const isReviewTab = status === "review";

  const startBackfill = async ({ force = false } = {}) => {
    const baseMsg =
      "Kick off the full Wikimedia Commons backfill? This walks every root " +
      "category (~1 req/sec) and can take several hours. Photos that fail " +
      "the sensitivity heuristic land in this Review tab.";
    const forceMsg =
      "\n\nFORCE MODE: This bypasses the same-day cache and re-scrapes " +
      "every category even if it was already processed since 00:00 UTC. " +
      "Use only when recovering from a ghost run or after a scraper " +
      "code change — otherwise you'll waste Commons rate-limit budget " +
      "for no new data.";
    const msg = force ? baseMsg + forceMsg : baseMsg;
    if (!window.confirm(msg)) return;
    setScrapeStarting(true);
    try {
      const url = force
        ? "/admin/history/scrape-start?force=true"
        : "/admin/history/scrape-start";
      await api.post(url);
      toast.success(
        force
          ? "Force backfill started — bypassing same-day cache"
          : "Backfill started in background — check back periodically"
      );
      // Immediately re-poll so the UI flips to the "in progress" banner
      // without waiting the full 12s tick.
      setTimeout(async () => {
        try {
          const r = await api.get("/admin/history/scrape-runs/current");
          setCurrentRun(r.data?.run || null);
          prevStatusRef.current = r.data?.run?.status || null;
        } catch (_e) { /* ignore */ }
      }, 800);
    } catch (e) {
      // 409 = another scrape is already running. Show that as a
      // targeted message rather than a generic "failed" toast.
      if (e?.response?.status === 409) {
        const d = e.response.data?.detail || {};
        toast.error(
          d.message || "A scrape is already running — check the progress banner"
        );
        // Force a poll so the banner appears even if the periodic
        // poller hasn't ticked yet.
        try {
          const r = await api.get("/admin/history/scrape-runs/current");
          setCurrentRun(r.data?.run || null);
        } catch (_e) { /* ignore */ }
      } else {
        toast.error("Backfill failed to start");
      }
    } finally {
      setScrapeStarting(false);
    }
  };

  const runSeed = async () => {
    if (!window.confirm("Run the ~30-photo preview seed? Idempotent — safe to re-run.")) return;
    try {
      const r = await api.post("/admin/history/seed");
      toast.success(`Seed complete — ${r.data?.seeded_total || 0} photos processed`);
      load();
    } catch (e) {
      toast.error("Seed failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="history-review-tab">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-[#1A1A1A]">
            Historical Photo Archive — Review Queue
          </h2>
          <p className="text-sm text-[#6B7280] max-w-2xl mt-1">
            Photos flagged by the three-axis sensitivity heuristic (era ·
            subject class · consent context). Approve to publish to
            <code className="text-xs px-1 mx-1 bg-[#F3F4F6] rounded">/history</code>
            or reject to hide permanently. Auto-blocked items are hard
            fails from the blocklist — review before manually approving.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="px-3 py-1.5 border border-[#E5E7EB] rounded text-sm inline-flex items-center gap-1.5 hover:bg-[#F9FAFB]"
            data-testid="history-refresh-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={runSeed}
            className="px-3 py-1.5 border border-[#E5E7EB] rounded text-sm inline-flex items-center gap-1.5 hover:bg-[#F9FAFB]"
            data-testid="history-seed-btn"
          >
            <ImageIcon size={14} /> Run seed (preview)
          </button>
          <button
            onClick={() => startBackfill()}
            disabled={scrapeStarting || isRunning}
            className="px-3 py-1.5 bg-[#0AADA0] text-white rounded text-sm inline-flex items-center gap-1.5 hover:bg-[#089489] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="history-backfill-btn"
          >
            <PlayCircle size={14} />{" "}
            {isRunning
              ? "Backfill in progress…"
              : scrapeStarting
              ? "Starting…"
              : "Start full backfill"}
          </button>
          <button
            onClick={() => startBackfill({ force: true })}
            disabled={scrapeStarting || isRunning}
            title="Bypass the same-day category cache (rescrape everything)"
            className="px-3 py-1.5 border border-[#0AADA0] text-[#0AADA0] rounded text-sm inline-flex items-center gap-1.5 hover:bg-[#E6F7F5] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="history-backfill-force-btn"
          >
            <PlayCircle size={14} /> Force re-scrape
          </button>
        </div>
      </div>

      {/* Live backfill progress banner. Renders whenever there's a
          `running` scrape, OR for ~2 min after a completed/failed run
          so the operator sees the outcome even if they refresh the
          page. Backend gates concurrent runs (returns 409), so this
          banner is authoritative — no UI-only "starting" state can
          leak past it. */}
      {currentRun && (
        <ScrapeProgressBanner
          run={currentRun}
          onMarkFailed={async () => {
            const runId = currentRun.run_id;
            if (!runId) return;
            const ok = window.confirm(
              "Force-mark this scrape run as failed?\n\n" +
              "Only do this if you're sure the worker is dead " +
              "(e.g. observed a pod restart). This unlocks the " +
              "backfill queue so a new run can be started."
            );
            if (!ok) return;
            try {
              await api.post(`/admin/history/scrape-runs/${runId}/mark-failed`);
              toast.success("Run marked as failed — you can now start a new backfill");
              // Immediately re-poll so the banner flips.
              const r = await api.get("/admin/history/scrape-runs/current");
              setCurrentRun(r.data?.run || null);
              prevStatusRef.current = r.data?.run?.status || null;
            } catch (e) {
              toast.error(
                e?.response?.data?.detail ||
                "Failed to mark run as failed"
              );
            }
          }}
          onRetryWithForce={() => startBackfill({ force: true })}
        />
      )}

      {/* Status filter tabs */}
      <div
        className="flex flex-wrap gap-1 border-b border-[#E5E7EB]"
        data-testid="history-status-tabs"
      >
        {STATUS_TABS.map((s) => {
          const active = status === s.id;
          return (
            <button
              key={s.id}
              onClick={() => {
                setStatus(s.id);
                setIsland("");
                setDecade("");
                setReason("");
                setOffset(0);
              }}
              data-testid={`history-status-${s.id}`}
              className={`px-4 py-2 text-xs uppercase tracking-wider border-b-2 transition-colors ${
                active
                  ? "border-[#0AADA0] text-[#0AADA0]"
                  : "border-transparent text-[#6B7280] hover:text-[#1A1A1A]"
              }`}
            >
              {s.label}
              {active && (
                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-mono bg-[#0AADA0]/10 rounded">
                  {total}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filter bar — island / decade / reason. Facet counts come from
          `/queue/facets` and always reflect the current status filter
          (never the currently-applied narrower filters — otherwise the
          admin can't broaden a filter without clearing it first). */}
      <div
        className="flex flex-wrap items-center gap-3 px-3 py-2 rounded bg-white border border-[#E5E7EB]"
        data-testid="history-filter-bar"
      >
        <span className="text-[11px] uppercase tracking-wider text-[#6B7280]">Filter</span>
        <select
          value={island}
          onChange={(e) => { setIsland(e.target.value); setOffset(0); }}
          className="px-2 py-1 border border-[#E5E7EB] rounded text-sm"
          data-testid="history-filter-island"
        >
          <option value="">All islands ({facets.islands.reduce((s, f) => s + f.count, 0)})</option>
          {facets.islands.map((f) => (
            <option key={f.value} value={f.value}>
              {f.value} ({f.count})
            </option>
          ))}
        </select>

        <select
          value={decade}
          onChange={(e) => { setDecade(e.target.value); setOffset(0); }}
          className="px-2 py-1 border border-[#E5E7EB] rounded text-sm"
          data-testid="history-filter-decade"
        >
          <option value="">All decades</option>
          {facets.decades.map((f) => (
            <option key={f.value} value={f.value}>
              {f.value}s ({f.count})
            </option>
          ))}
        </select>

        <select
          value={reason}
          onChange={(e) => { setReason(e.target.value); setOffset(0); }}
          className="px-2 py-1 border border-[#E5E7EB] rounded text-sm"
          data-testid="history-filter-reason"
        >
          <option value="">All reasons</option>
          {facets.reason_buckets.length > 0 && (
            <optgroup label="Buckets">
              {facets.reason_buckets.map((f) => (
                <option key={`b:${f.value}`} value={`${f.value}:`}>
                  {f.value}:* ({f.count})
                </option>
              ))}
            </optgroup>
          )}
          {facets.reasons_granular.length > 0 && (
            <optgroup label="Specific">
              {facets.reasons_granular.map((f) => (
                <option key={`g:${f.value}`} value={f.value}>
                  {f.value} ({f.count})
                </option>
              ))}
            </optgroup>
          )}
        </select>

        {(island || decade || reason) && (
          <button
            onClick={() => {
              setIsland("");
              setDecade("");
              setReason("");
              setOffset(0);
            }}
            className="text-xs text-[#6B7280] hover:text-[#1A1A1A] underline"
            data-testid="history-filter-clear"
          >
            Clear filters
          </button>
        )}

        <span className="ml-auto text-xs text-[#6B7280] font-mono">
          {total > 0 ? (
            <>
              <span data-testid="history-filter-total">
                {offset + 1}-{Math.min(offset + items.length, total)}
              </span>{" "}
              of {total}
            </>
          ) : (
            <span data-testid="history-filter-total">0</span>
          )}
        </span>
      </div>

      {/* Bulk action bar — only visible on the Needs Review tab */}
      {isReviewTab && items.length > 0 && (
        <div
          className="flex items-center justify-between gap-3 flex-wrap px-3 py-2 rounded bg-[#F9FAFB] border border-[#E5E7EB]"
          data-testid="history-bulk-bar"
        >
          <button
            onClick={toggleAllOnPage}
            className="text-xs inline-flex items-center gap-2 text-[#1A1A1A] hover:text-[#0AADA0]"
            data-testid="history-select-all"
          >
            {allOnPageSelected ? (
              <CheckSquare size={16} className="text-[#0AADA0]" />
            ) : (
              <Square size={16} className="text-[#6B7280]" />
            )}
            {allOnPageSelected ? "Deselect all on page" : `Select all on page (${items.length})`}
          </button>

          <div className="flex items-center gap-2">
            <span
              className="text-xs font-mono text-[#6B7280]"
              data-testid="history-selected-count"
            >
              {selected.size} selected
            </span>
            <button
              onClick={() => bulkDecide("approve")}
              disabled={selected.size === 0 || bulkPending}
              className="px-3 py-1.5 bg-[#0AADA0] text-white rounded text-xs inline-flex items-center gap-1 hover:bg-[#089489] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="history-bulk-approve"
            >
              <Check size={12} /> {bulkPending ? "…" : `Approve ${selected.size || ""}`}
            </button>
            <button
              onClick={() => bulkDecide("reject")}
              disabled={selected.size === 0 || bulkPending}
              className="px-3 py-1.5 border border-[#E5E7EB] rounded text-xs inline-flex items-center gap-1 hover:bg-[#F9FAFB] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="history-bulk-reject"
            >
              <X size={12} /> {bulkPending ? "…" : `Reject ${selected.size || ""}`}
            </button>
            {selected.size > 0 && !bulkPending && (
              <button
                onClick={() => setSelected(new Set())}
                className="px-2 py-1.5 text-xs text-[#6B7280] hover:text-[#1A1A1A]"
                data-testid="history-clear-selection"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {loading && <div className="text-sm text-[#6B7280]">Loading…</div>}
      {!loading && items.length === 0 && (
        <div
          className="p-8 text-center text-[#6B7280] border border-dashed border-[#E5E7EB] rounded"
          data-testid="history-empty-state"
        >
          {(island || decade || reason)
            ? "No photos match the current filters. Try clearing them or widening the selection."
            : isReviewTab
            ? "No photos waiting for review. The queue is clear."
            : `No ${status} photos.`}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((p) => (
          <PhotoReviewCard
            key={p.id}
            photo={p}
            onApprove={() => decide(p.id, "approve")}
            onReject={() => decide(p.id, "reject")}
            onToggleSelect={() => toggleOne(p.id)}
            selected={selected.has(p.id)}
            selectable={isReviewTab}
            pending={pending[p.id]}
            readOnly={!isReviewTab}
          />
        ))}
      </div>

      {/* Pagination — offset navigation. Total is server-authoritative
          so this stays honest even when a parallel admin is also
          approving/rejecting items. `currentPage` and `totalPages` are
          computed rather than stored to avoid drift after bulk actions
          shrink `total`. */}
      {total > PAGE_SIZE && (
        <div
          className="flex items-center justify-between gap-3 px-3 py-2 rounded bg-white border border-[#E5E7EB]"
          data-testid="history-pagination"
        >
          <button
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            disabled={offset === 0 || loading}
            className="px-3 py-1.5 border border-[#E5E7EB] rounded text-xs inline-flex items-center gap-1 hover:bg-[#F9FAFB] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="history-page-prev"
          >
            ← Prev
          </button>
          <span className="text-xs text-[#6B7280] font-mono">
            Page{" "}
            <span data-testid="history-page-current">
              {Math.floor(offset / PAGE_SIZE) + 1}
            </span>{" "}
            of{" "}
            <span data-testid="history-page-total">
              {Math.max(1, Math.ceil(total / PAGE_SIZE))}
            </span>
          </span>
          <button
            onClick={() =>
              setOffset((o) =>
                o + PAGE_SIZE < total ? o + PAGE_SIZE : o
              )
            }
            disabled={offset + PAGE_SIZE >= total || loading}
            className="px-3 py-1.5 border border-[#E5E7EB] rounded text-xs inline-flex items-center gap-1 hover:bg-[#F9FAFB] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="history-page-next"
          >
            Next →
          </button>
        </div>
      )}

      {/* Scrape state checkpoints */}
      {scrapeState.length > 0 && (
        <details className="border border-[#E5E7EB] rounded-lg" data-testid="history-scrape-state">
          <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-[#1A1A1A] hover:bg-[#F9FAFB]">
            Scrape state — last {scrapeState.length} checkpoints
          </summary>
          <div className="border-t border-[#E5E7EB] divide-y divide-[#F3F4F6]">
            {scrapeState.map((s, i) => (
              <div key={i} className="px-4 py-2 text-xs grid grid-cols-4 gap-3 items-center">
                <span className="uppercase tracking-wider text-[#6B7280]">
                  {s.island || "—"}
                </span>
                <span className="text-[#1A1A1A] truncate">{s.category}</span>
                <span className="text-[#6B7280] font-mono">
                  {s.stats?.inserted != null &&
                    `+${s.stats.inserted} new · ${s.stats.updated} upd · ${s.stats.skipped} skip`}
                </span>
                <span className="text-[#9CA3AF] text-right">
                  {s.completed_at?.slice(0, 19).replace("T", " ")}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const SENSITIVITY_COLORS = {
  auto_publish: "#0AADA0",
  review: "#F59E0B",
  reject: "#EF4444",
};

const PhotoReviewCard = ({
  photo,
  onApprove,
  onReject,
  onToggleSelect,
  selected,
  selectable,
  pending,
  readOnly,
}) => {
  const color = SENSITIVITY_COLORS[photo.sensitivity_score] || "#6B7280";
  const desc =
    photo.descriptions?.en ||
    photo.descriptions?.nl ||
    photo.descriptions?.es ||
    "";
  return (
    <div
      className={`border rounded-lg bg-white overflow-hidden flex flex-col transition-colors ${
        selected ? "border-[#0AADA0] ring-1 ring-[#0AADA0]/30" : "border-[#E5E7EB]"
      }`}
      data-testid={`history-review-card-${photo.id}`}
    >
      {/* Image — direct-hosted from Commons (never proxied through us).
          Selection checkbox overlaid top-left when the card is selectable. */}
      <div className="relative">
        {selectable && (
          <button
            onClick={onToggleSelect}
            className="absolute top-2 left-2 z-10 bg-white/95 rounded p-1.5 shadow-sm hover:bg-white"
            data-testid={`history-select-${photo.id}`}
            aria-label={selected ? "Deselect photo" : "Select photo"}
          >
            {selected ? (
              <CheckSquare size={18} className="text-[#0AADA0]" />
            ) : (
              <Square size={18} className="text-[#6B7280]" />
            )}
          </button>
        )}
        <a
          href={photo.commons_url_page}
          target="_blank"
          rel="noreferrer noopener"
          className="block bg-[#F3F4F6] aspect-video overflow-hidden group"
        >
          {photo.thumb_url ? (
            <img
              src={photo.thumb_url}
              alt={photo.commons_title || ""}
              loading="lazy"
              className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]">
              <ImageIcon size={32} />
            </div>
          )}
        </a>
      </div>

      <div className="p-4 flex-1 flex flex-col gap-3">
        <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider">
          <span
            className="px-2 py-0.5 rounded font-mono"
            style={{ background: `${color}20`, color }}
          >
            {photo.sensitivity_score?.replace("_", " ")}
          </span>
          <span className="text-[#6B7280]">{photo.island}</span>
          {photo.year && <span className="text-[#6B7280]">· {photo.year}</span>}
          {photo.decade && (
            <span className="text-[#6B7280]">· {photo.decade}s</span>
          )}
          {photo.flags?.print_status && (
            <span className="ml-auto font-mono text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded">
              print: {photo.flags.print_status}
            </span>
          )}
        </div>

        <div className="text-sm font-medium text-[#1A1A1A] line-clamp-2">
          {photo.commons_title?.replace(/^File:/, "")}
        </div>

        {desc && (
          <div className="text-xs text-[#6B7280] line-clamp-3">{desc}</div>
        )}

        {/* Sensitivity reasons — the "why" for admin trust */}
        {photo.sensitivity_reasons?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {photo.sensitivity_reasons.map((r) => (
              <span
                key={r}
                className="text-[10px] px-1.5 py-0.5 rounded font-mono bg-[#FEF3C7] text-[#92400E]"
              >
                {r}
              </span>
            ))}
          </div>
        )}

        <div className="text-[10px] text-[#9CA3AF] flex flex-wrap gap-2">
          <span>{photo.license_short}</span>
          {photo.photographer && <span>· {photo.photographer}</span>}
          {photo.long_edge_px > 0 && (
            <span>
              · {photo.width}×{photo.height}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 mt-auto pt-2">
          {!readOnly && (
            <>
              <button
                onClick={onApprove}
                disabled={!!pending}
                className="px-3 py-1.5 bg-[#0AADA0] text-white rounded text-xs inline-flex items-center gap-1 hover:bg-[#089489] disabled:opacity-50"
                data-testid={`history-approve-${photo.id}`}
              >
                <Check size={12} /> {pending === "approve" ? "…" : "Approve"}
              </button>
              <button
                onClick={onReject}
                disabled={!!pending}
                className="px-3 py-1.5 border border-[#E5E7EB] rounded text-xs inline-flex items-center gap-1 hover:bg-[#F9FAFB] disabled:opacity-50"
                data-testid={`history-reject-${photo.id}`}
              >
                <X size={12} /> {pending === "reject" ? "…" : "Reject"}
              </button>
            </>
          )}
          {readOnly && photo.admin_decision && (
            <span
              className="text-[10px] uppercase tracking-wider px-2 py-1 rounded font-mono"
              style={{
                background: photo.admin_decision === "approved" ? "#0AADA020" : "#EF444420",
                color: photo.admin_decision === "approved" ? "#0AADA0" : "#EF4444",
              }}
            >
              {photo.admin_decision}
            </span>
          )}
          <a
            href={photo.commons_url_page}
            target="_blank"
            rel="noreferrer noopener"
            className="ml-auto text-[10px] uppercase tracking-wider text-[#6B7280] hover:text-[#0AADA0] inline-flex items-center gap-1"
            data-testid={`history-commons-link-${photo.id}`}
          >
            View on Commons <ExternalLink size={10} />
          </a>
        </div>
      </div>
    </div>
  );
};

// Live progress banner for the currently-running (or most-recently-
// finished) scrape. Colors: teal for running, green for completed,
// red for failed, blue for skipped (no-op runs). Auto-hides finished
// runs after ~2 min so the banner doesn't clutter the tab forever.
const ScrapeProgressBanner = ({ run, onMarkFailed, onRetryWithForce }) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const isRunning = run.status === "running";
  const isFailed = run.status === "failed";
  const isSkipped = run.status === "skipped";
  const started = run.started_at ? new Date(run.started_at) : null;
  const finished = run.finished_at ? new Date(run.finished_at) : null;
  const elapsedMs = (finished || new Date()) - (started || new Date());
  const elapsedMin = Math.max(0, Math.floor(elapsedMs / 60000));

  // Heartbeat freshness — for running scrapes we surface how long ago
  // the worker last checkpointed. Anything > 5 min is a strong hint the
  // pod died and the operator should hit "Force mark failed" (backend
  // will also auto-recover on the next scrape-start, but the button
  // exists so the operator doesn't need to wait for that dispatch).
  const heartbeat = run.last_heartbeat_at ? new Date(run.last_heartbeat_at) : null;
  const hbAgeSec = heartbeat ? Math.floor((Date.now() - heartbeat.getTime()) / 1000) : null;
  const hbStale = hbAgeSec !== null && hbAgeSec > 300;

  // Auto-hide finished runs after 2 min. We compute this at render
  // time rather than via a useEffect timer so a manual refresh still
  // honours it — the banner is purely a peek, not a permanent log.
  if (!isRunning && finished && (Date.now() - finished.getTime()) > 120000) {
    return null;
  }

  const bg = isRunning
    ? (hbStale ? "#FEF3C7" : "#E6F7F5")
    : isFailed ? "#FEE2E2"
    : isSkipped ? "#DBEAFE"
    : "#DCFCE7";
  const border = isRunning
    ? (hbStale ? "#F59E0B" : "#0AADA0")
    : isFailed ? "#EF4444"
    : isSkipped ? "#3B82F6"
    : "#22C55E";
  const label = isRunning
    ? (hbStale ? "Backfill stalled — worker may be dead" : "Backfill in progress")
    : isFailed ? "Backfill failed"
    : isSkipped ? "Backfill was a no-op — daily cache hit"
    : "Backfill complete";

  return (
    <div
      className="border rounded-lg px-4 py-3"
      style={{ backgroundColor: bg, borderColor: border }}
      data-testid="history-scrape-banner"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[#1A2E35] mb-0.5" data-testid="history-scrape-banner-label">
            {label}
            {isRunning && !hbStale && (
              <span className="ml-2 inline-block w-2 h-2 rounded-full bg-[#0AADA0] animate-pulse" />
            )}
          </div>
          {isRunning && run.current_category && (
            <div className="text-xs text-[#3D4C52]" data-testid="history-scrape-banner-current">
              Processing:{" "}
              <span className="font-mono text-[#0AADA0]">
                {run.current_island} · {run.current_category}
              </span>
              {" — "}
              <span>{run.per_category_count || 0} categories done</span>
            </div>
          )}
          {isRunning && hbAgeSec !== null && (
            <div
              className={`text-xs mt-0.5 font-mono ${hbStale ? "text-[#92400E]" : "text-[#3D4C52]"}`}
              data-testid="history-scrape-banner-heartbeat"
            >
              Last heartbeat: {hbAgeSec}s ago
              {hbStale && " · worker likely dead (>5m)"}
            </div>
          )}
          {isFailed && run.error && (
            <div className="text-xs text-[#7F1D1D] font-mono truncate" data-testid="history-scrape-banner-error">
              {run.error}
            </div>
          )}
          {isSkipped && (
            <div className="text-xs text-[#1E3A8A] mt-0.5" data-testid="history-scrape-banner-skipped-reason">
              {run.reason ||
                "Every category was already scraped since 00:00 UTC today. This is expected on repeat clicks — Commons content doesn't change fast enough to justify multiple full runs per day. Use Force re-scrape to override (e.g. after a scraper code change)."}
            </div>
          )}
          <div className="text-[11px] text-[#6B7C82] mt-1 font-mono">
            {isRunning ? "Elapsed" : "Duration"}: {elapsedMin}m
            {" · "}
            +{run.totals?.inserted || 0} new
            {" · "}
            {run.totals?.updated || 0} updated
            {" · "}
            {run.totals?.skipped || 0} skipped
            {run.run_id ? ` · run ${run.run_id}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isRunning && onMarkFailed && (
            <button
              onClick={onMarkFailed}
              className="text-xs px-3 py-1.5 rounded border border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
              data-testid="history-scrape-banner-mark-failed"
            >
              Force mark failed
            </button>
          )}
          {isSkipped && onRetryWithForce && (
            <button
              onClick={onRetryWithForce}
              className="text-xs px-3 py-1.5 rounded bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors"
              data-testid="history-scrape-banner-retry-force"
            >
              Retry with force
            </button>
          )}
          {!isRunning && (
            <button
              onClick={() => setDismissed(true)}
              className="text-xs text-[#6B7C82] hover:text-[#1A2E35]"
              data-testid="history-scrape-banner-dismiss"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
